import React, { useState, useEffect, useCallback, useMemo } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import swal from "sweetalert";
import AddPaymentGatewayModal from "./addpaymentMethodModel";
import ThemeButton from "../../components/CustomComponents/Buttons/ThemeButton/ThemeButton";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import ModernGridToolbar from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";
import { setVisibleColumns, setFilterField } from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import {
  applyCatalogTotalFromFetch,
  applyCatalogTotalFromMutation,
} from "../../utils/catalogTotalRecords";



const COLUMN_ORDER = ["name", "hostUrl", "status", "entryDate"];

const formatEntryDate = (dateVal) => {
  if (!dateVal) return "—";
  try {
    const d = new Date(dateVal);
    if (Number.isNaN(d.getTime())) return String(dateVal);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(dateVal);
  }
};

const ManagePaymentGateways = () => {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const dispatch = useDispatch();
  const pageKey = "paymentGateways";
  const defaultVisibleColumns = {
    name: true,
    hostUrl: true,
    status: true,
    entryDate: true,
  };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;

  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [paymentGateways, setPaymentGateways] = useState([]);
  const [search, setSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [gatewayToEdit, setGatewayToEdit] = useState(null);

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const fetchPaymentGateways = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_PAYMENT_GATEWAYS, {
        params: { CompID },
      });
      const rows = response.data.data || [];
      setPaymentGateways(rows);
      applyCatalogTotalFromFetch(setCatalogTotalRecords, rows.length);
    } catch (err) {
      setPaymentGateways([]);
      showErrorToast("Failed to load payment gateways");
    } finally {
      setLoading(false);
      setInitialLoadComplete(true);
    }
  }, [CompID]);

  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
      dispatch(
        setVisibleColumns({
          pageKey,
          columns: mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns),
        })
      );
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  useEffect(() => {
    fetchPaymentGateways();
  }, [fetchPaymentGateways]);

  useResetPageOnFilterChange(setPageNumber, [search, pageSize?.value]);

  const filteredGateways = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return paymentGateways;
    return paymentGateways.filter((g) => {
      const haystack = [
        g.pgName,
        g.pgHostURL,
        g.status ? "active" : "inactive",
        g.enteryDateTime,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [paymentGateways, search]);

  const totalRecords = filteredGateways.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / Number(pageSize.value)));
  const pagedGateways = useMemo(() => {
    const size = Number(pageSize.value);
    const start = (pageNumber - 1) * size;
    return filteredGateways.slice(start, start + size);
  }, [filteredGateways, pageNumber, pageSize]);

  useEffect(() => {
    if (pageNumber > totalPages) setPageNumber(totalPages);
  }, [pageNumber, totalPages]);

  const handleDeleteGateway = async (gateway) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${gateway.pgName}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) return;

    try {
      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_PAYMENT_GATEWAY, {
        params: {
          PG_ID: gateway.id,
          CompID,
        },
      });
      if (response.data.status) {
        setPaymentGateways((prev) => {
          const next = prev.filter((g) => g.id !== gateway.id);
          applyCatalogTotalFromMutation(setCatalogTotalRecords, next.length);
          return next;
        });
        showSuccessToast("Payment gateway deleted successfully");
      }
    } catch (err) {
      showErrorToast("Failed to delete payment gateway");
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentGatewaySubmit = (payload, isUpdate) => {
    if (isUpdate) {
      setPaymentGateways((prev) =>
        prev.map((gateway) =>
          gateway.id === payload.ID ? { ...gateway, ...payload } : gateway
        )
      );
    } else {
      fetchPaymentGateways();
    }
  };

  const handleEditGateway = (gateway) => {
    setGatewayToEdit({
      ID: gateway.id,
      PGName: gateway.pgName,
      PGHostURL: gateway.pgHostURL,
      PGAPIPriKey: gateway.pgapiPriKey,
      PGAPISecKey: gateway.pgapiSecKey,
      Status: gateway.status !== false,
      EnteryDateTime: gateway.enteryDateTime,
      PlatformID: gateway.platformID || gateway.platformId || null,
      AllowGatewayRefunds: gateway.allowGatewayRefunds !== false,
    });
    setIsModalOpen(true);
  };

  const handleAddClick = () => {
    setGatewayToEdit(null);
    setIsModalOpen(true);
  };

  const hasFiltersApplied = () => search.trim() !== "";
  const clearFilters = () => setSearch("");

  const showEmptyCatalogState =
    initialLoadComplete &&
    !loading &&
    !hasFiltersApplied() &&
    catalogTotalRecords === 0;

  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No payment gateways match "${search.trim()}". Try different keywords or clear filters.`;
    }
    return "There are no payment gateways to show. Add a gateway to accept card payments.";
  };

  const stickyHeaderStyle = {
    position: "sticky",
    top: 0,
    zIndex: 10000,
    backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
    color: isDarkMode ? "#ffffff" : "#000000",
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Payment Gateways</li>
          </ol>
          <h1 className="page-header mb-0">Manage Payment Gateways</h1>
        </div>
        <div className="ms-auto">
          <ThemeButton icon="fa fa-plus" onClick={handleAddClick}>
            Add Payment Gateway
          </ThemeButton>
        </div>
      </div>

      <AddPaymentGatewayModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        onPaymentGatewaySubmit={handlePaymentGatewaySubmit}
        compID={CompID}
        paymentGatewayToEdit={gatewayToEdit}
        setPaymentGateways={setPaymentGateways}
      />

      <div className="mb-3 mt-2">
        {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

        {showEmptyCatalogState && (
          <AddButton onClick={handleAddClick} title="Add Payment Gateway" />
        )}

        {showGridToolbar && (
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Payment Gateways"
            totalCount={totalRecords}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by name, URL, status..."
            bodyFlush
            headerControls={
              <ColumnSelection
                visibleColumns={visibleColumns}
                columnOrder={COLUMN_ORDER}
                setVisibleColumns={(cols) =>
                  dispatch(setVisibleColumns({ pageKey, columns: cols }))
                }
              />
            }
          >
            <div className="px-3 pb-3">
              <GridSectionLoader loading={loading} minHeight="280px">
                {!loading && pagedGateways.length === 0 && (
                  <GridEmptyState
                    isDarkMode={isDarkMode}
                    themeColor={themeColor}
                    title="No payment gateways found"
                    description={getEmptyStateDescription()}
                    onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
                    clearFiltersLabel="Clear all filters"
                  />
                )}

                {!loading && pagedGateways.length > 0 && (
                  <div
                    className="table-responsive table-container"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <table className="table table-hover">
                      <thead className="bg-light sticky-header" style={stickyHeaderStyle}>
                        <tr>
                          {visibleColumns.name && <th className="pt-2 pb-2">Name</th>}
                          {visibleColumns.hostUrl && (
                            <th className="pt-2 pb-2">Host URL</th>
                          )}
                          {visibleColumns.status && <th className="pt-2 pb-2">Status</th>}
                          {visibleColumns.entryDate && (
                            <th className="pt-2 pb-2">Entry Date</th>
                          )}
                          <th className="pt-2 pb-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedGateways.map((gateway) => (
                          <tr key={gateway.id}>
                            {visibleColumns.name && (
                              <td
                                className="align-middle"
                                onClick={() => handleEditGateway(gateway)}
                                style={{ cursor: "pointer" }}
                              >
                                {gateway.pgName || "—"}
                              </td>
                            )}
                            {visibleColumns.hostUrl && (
                              <td
                                className="align-middle"
                                onClick={() => handleEditGateway(gateway)}
                                style={{ cursor: "pointer" }}
                              >
                                {gateway.pgHostURL || "—"}
                              </td>
                            )}
                            {visibleColumns.status && (
                              <td className="align-middle">
                                <span
                                  className={`badge ${
                                    gateway.status ? "bg-success" : "bg-secondary"
                                  }`}
                                >
                                  {gateway.status ? "Active" : "Inactive"}
                                </span>
                              </td>
                            )}
                            {visibleColumns.entryDate && (
                              <td className="align-middle">
                                {formatEntryDate(gateway.enteryDateTime)}
                              </td>
                            )}
                            <td className="align-middle text-center">
                              <TableActionsMenu menuEnd>
                                <TableActionsItem
                                  icon="fa fa-edit"
                                  onClick={() => handleEditGateway(gateway)}
                                >
                                  Edit
                                </TableActionsItem>
                                <TableActionsItem
                                  danger
                                  icon="fa fa-trash"
                                  onClick={() => handleDeleteGateway(gateway)}
                                >
                                  Delete
                                </TableActionsItem>
                              </TableActionsMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <ModernGridPagerFooter
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  totalRecords={totalRecords}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                />
              </GridSectionLoader>
            </div>
          </ModernGridToolbar>
        )}
      </div>
    </div>
  );
};

export default ManagePaymentGateways;
